import React from 'react';
import Notification from '../components/Notification';
import WaitForFiles from '../components/WaitForFiles';
import WaitForExternalProcess from '../components/WaitForExternalProcess';
import WaitForEmailForm from '../components/WaitForEmailForm';
import { Card, CardBody } from '../components/ui';
import FormSkeleton from '../components/FormSkeleton';
import useResourceLoader from '../util/useResourceLoader';
import FormApi from '../api/FormApi';
import UWEEntityApi from '../api/UWEEntityApi';
import UWEEntityUWEFormComponent from './UWEEntityUWEFormComponent';
import classnames from 'src/util/classnames';
import { Trans } from '@lingui/react/macro';

function CurrentUWEEntityForm({
  hide,
  slug,
  viewForm,
  currentEntity,
  currentStatus,
  dataRemarks,
  loadingDataRemarks,
  onSubmit,
  hideSideTabs,
  wfAccessToken,
  setCurrentEntity,
  propForm,
  resolvedFormVersions: propResolvedFormVersions,
}) {
  const { page: _formName, type, awaitDocuments, isOpen } = currentStatus || {};
  const formName = (isOpen ? _formName : null) || viewForm;

  const isDocumentStep = awaitDocuments || type === 'documentGeneration';
  const isExternalProcessStep = type === 'externalProcess';
  const isEmailForm = type === 'emailForm';
  const isFormStep = !isDocumentStep && !isExternalProcessStep;

  // Most callers (e.g. details.jsx) now resolve the form themselves and pass it as `propForm`
  // (plus `resolvedFormVersions`, for this page's own subforms) — this only does its own
  // resolving as a fallback for callers that don't, so the same version-aware resolution still
  // applies to them too. Resolves formName + every SubformField it references (recursively)
  // under this entity's effective FormVersioningMode, in one call.
  const [loaded, loadingISF = true, errorLoadingISF] = useResourceLoader(async () => {
    if (propForm) return { content: propForm, resolvedFormVersions: propResolvedFormVersions };
    if (!isFormStep || !formName || !currentEntity?.id) return null;

    const resolvedFormVersions = await UWEEntityApi.resolveFormVersions({ entityId: currentEntity.id, formpage: formName, wfAccessToken });
    const resolvedPage = resolvedFormVersions?.[formName];
    // Unresolved (e.g. Latest/Pinned mode with nothing published yet) — content stays null,
    // which renders the same "not fully implemented yet" notice as an unresolvable form always has.
    const content = resolvedPage ? await FormApi.getVersion({ formId: resolvedPage.formId, version: resolvedPage.version }) : null;

    return { content, resolvedFormVersions };
  }, [propForm, propResolvedFormVersions, formName, currentEntity?.id, wfAccessToken]);

  const form = loaded?.content;
  const resolvedFormVersions = loaded?.resolvedFormVersions;

  const id = currentEntity?.id ? { id: `entity-form-${currentEntity?.id}` } : {};
  return (
    <div {...id} className={classnames('uweentity-form h-full w-full', { hidden: hide })}>
      {isDocumentStep ? (
        <WaitForFiles currentEntity={currentEntity} page={formName} wfAccessToken={wfAccessToken} />
      ) : isExternalProcessStep ? (
        <WaitForExternalProcess type="externalProcess" currentEntity={currentEntity} slug={slug} viewForm={viewForm} wfAccessToken={wfAccessToken} />
      ) : isEmailForm ? (
        <WaitForEmailForm currentEntity={currentEntity} page={formName} wfAccessToken={wfAccessToken} />
      ) : formName ? (
        loadingISF ? (
          <Card style={{ height: '100%', overflowY: 'hidden' }}>
            <CardBody>
              <FormSkeleton title={<Trans>Loading your workflow</Trans>} message={<Trans>Preparing your form...</Trans>} />
            </CardBody>
          </Card>
        ) : errorLoadingISF ? (
          <Notification error={errorLoadingISF} />
        ) : form ? (
          <UWEEntityUWEFormComponent
            slug={slug}
            form={form}
            currentEntity={currentEntity}
            dataRemarks={dataRemarks}
            loadingDataRemarks={loadingDataRemarks}
            wfAccessToken={wfAccessToken}
            setCurrentEntity={setCurrentEntity}
            resolvedFormVersions={resolvedFormVersions}
          />
        ) : (
          <Notification color="warning">
            <Trans>Form not fully implemented yet</Trans>
          </Notification>
        )
      ) : (
        <Notification color="warning">
          <Trans>The entity does not have any forms that you can view.</Trans>
        </Notification>
      )}
    </div>
  );
}

export default CurrentUWEEntityForm;
